import type { AxiosError } from 'axios';
import type { OperationStep, StepCounts, StepStatus } from '@/types/operations';

const EMPTY_COUNTS: StepCounts = { PENDING: 0, PASSED: 0, FAILED: 0, EXEMPTED: 0 };

const STEP_STATUSES: StepStatus[] = ['PENDING', 'PASSED', 'FAILED', 'EXEMPTED'];

/** Recompute per-status counts from a list of steps (after a local single-step update). */
export const computeStepCounts = (steps: OperationStep[]): StepCounts => {
  const counts: StepCounts = { ...EMPTY_COUNTS };
  for (const step of steps) {
    if (STEP_STATUSES.includes(step.status)) {
      counts[step.status] += 1;
    }
  }
  return counts;
};

/** A checklist can only be completed once every step is PASSED or EXEMPTED. */
export const isChecklistResolvable = (counts: StepCounts): boolean =>
  counts.PENDING === 0 && counts.FAILED === 0;

interface ApiErrorBody {
  detail?: string;
  reason?: string[];
  unresolved_steps?: string[];
}

const getErrorBody = (error: unknown): ApiErrorBody | undefined =>
  (error as AxiosError<ApiErrorBody>)?.response?.data;

/** Pull the backend's `detail`/field message out of an axios error, falling back to a default. */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const data = getErrorBody(error);
  if (data) {
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.reason) && data.reason.length > 0) return data.reason[0];
  }
  return fallback;
};

/** Pull the `unresolved_steps` keys from a 400 complete-checklist response. */
export const getUnresolvedStepKeys = (error: unknown): string[] => {
  const data = getErrorBody(error);
  return data && Array.isArray(data.unresolved_steps) ? data.unresolved_steps : [];
};
